// Real-time pitch detection for the instrument tuners. Runs entirely on audio
// already captured from the microphone, no server round-trip.
//
// Algorithm: autocorrelation with silence-trimming and a parabolic-interpolated
// peak (the "ACF2+" approach used by most browser tuner demos since the early
// Web Audio API days). Autocorrelation beats FFT-bin picking here because a
// plucked string's fundamental is often quieter than its own harmonics, so the
// loudest FFT bin is frequently the wrong octave; autocorrelation finds the
// repeating period directly and is stable well below 100 Hz on a small buffer.

export function detectPitch(buffer: Float32Array, sampleRate: number): number | null {
  const size = buffer.length;

  let rms = 0;
  for (let i = 0; i < size; i++) rms += buffer[i] * buffer[i];
  rms = Math.sqrt(rms / size);
  if (rms < 0.01) return null; // silence / just noise floor

  // Trim the near-zero head and tail so autocorrelation isn't dragged around by
  // a quiet lead-in or fade-out that doesn't share the note's periodicity.
  const threshold = 0.2;
  let start = 0;
  for (let i = 0; i < size / 2; i++) {
    if (Math.abs(buffer[i]) < threshold) {
      start = i;
      break;
    }
  }
  let end = size - 1;
  for (let i = 1; i < size / 2; i++) {
    if (Math.abs(buffer[size - i]) < threshold) {
      end = size - i;
      break;
    }
  }

  const trimmed = buffer.subarray(start, end);
  const n = trimmed.length;
  if (n < 8) return null;

  const correlation = new Float32Array(n);
  for (let lag = 0; lag < n; lag++) {
    let sum = 0;
    for (let i = 0; i < n - lag; i++) sum += trimmed[i] * trimmed[i + lag];
    correlation[lag] = sum;
  }

  // The correlation starts at its max (lag 0) and first has to fall before the
  // real periodic peak appears; skip past that initial downslope.
  let d = 0;
  while (d < n - 1 && correlation[d] > correlation[d + 1]) d++;

  let maxValue = -1;
  let maxLag = -1;
  for (let lag = d; lag < n; lag++) {
    if (correlation[lag] > maxValue) {
      maxValue = correlation[lag];
      maxLag = lag;
    }
  }
  if (maxLag <= 0) return null;

  // Parabolic interpolation around the integer-lag peak for sub-sample accuracy,
  // the difference between a tuner that's musically usable and one that just
  // jitters between two adjacent semitones.
  let bestLag = maxLag;
  if (maxLag > 0 && maxLag < n - 1) {
    const x1 = correlation[maxLag - 1];
    const x2 = correlation[maxLag];
    const x3 = correlation[maxLag + 1];
    const a = (x1 + x3 - 2 * x2) / 2;
    const b = (x3 - x1) / 2;
    if (a !== 0) bestLag = maxLag - b / (2 * a);
  }

  if (bestLag <= 0) return null;
  const freq = sampleRate / bestLag;
  return Number.isFinite(freq) ? freq : null;
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export interface DetectedNote {
  name: string;
  octave: number;
  cents: number;
}

// A4 = 440 Hz is standard concert pitch; MIDI note 69.
export function freqToNote(freq: number): DetectedNote {
  const midi = 69 + 12 * Math.log2(freq / 440);
  const rounded = Math.round(midi);
  const cents = Math.round((midi - rounded) * 100);
  const name = NOTE_NAMES[((rounded % 12) + 12) % 12];
  const octave = Math.floor(rounded / 12) - 1;
  return { name, octave, cents };
}

export function centsOffFrom(freq: number, targetFreq: number): number {
  return Math.round(1200 * Math.log2(freq / targetFreq));
}
